import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { LogoSpotlight } from '#app/components/logo-spotlight.tsx'
import { SpotlightStage } from '#app/components/spotlight-stage.tsx'
import { Badge } from '#app/components/ui/badge.tsx'
import { Button } from '#app/components/ui/button.tsx'
import {
	ChapterStage,
	chapterNumber,
} from '#app/components/ui/chapter-stage.tsx'
import { glassControlClassName } from '#app/components/ui/glass.ts'
import { IconChip } from '#app/components/ui/icon-chip.tsx'
import { Icon, type IconName } from '#app/components/ui/icon.tsx'
import { Magnetic } from '#app/components/ui/magnetic.tsx'
import { PaintedText } from '#app/components/ui/painted-text.tsx'
import { Reveal } from '#app/components/ui/reveal.tsx'
import { WordReveal } from '#app/components/ui/word-reveal.tsx'
import { useLocalizedPath } from '#app/utils/i18n.ts'
import { cn } from '#app/utils/misc.tsx'
import {
	generateMarketingMeta,
	generateOrganizationJsonLd,
	getLayoutSeoData,
} from '#app/utils/seo.ts'
import { trackEvent } from '#app/utils/tracking.ts'
import { CtaBand } from './+marketing-components/cta-band.tsx'
import { type Route } from './+types/index.ts'

export const meta: Route.MetaFunction = ({ matches }) => {
	const { siteUrl, lang } = getLayoutSeoData(matches)
	return [
		...generateMarketingMeta({
			title: 'Open Sourced: Hosting, Cloudron & Privacy aus der Schweiz',
			description:
				'Open Sourced bietet Webseiten, Managed Cloudron Hosting und Privacy-Beratung, alles aus der Schweiz.',
			siteUrl,
			lang,
			path: '/',
		}),
		{ 'script:ld+json': generateOrganizationJsonLd(siteUrl) },
	]
}

const trustItems = [
	{ icon: 'code' as IconName, labelKey: 'home.trust.openSource' as const },
	{ icon: 'shield-check' as IconName, labelKey: 'home.trust.privacy' as const },
	{ icon: 'server' as IconName, labelKey: 'home.trust.swissServers' as const },
	{
		icon: 'avatar' as IconName,
		labelKey: 'home.trust.personalSupport' as const,
	},
]

type Offering = {
	key:
		| 'nextcloud'
		| 'cloudron'
		| 'websites'
		| 'webProjects'
		| 'coaching'
		| 'shop'
	icon: IconName
	to: string
	external?: boolean
	badgeKey?: 'home.offerings.coaching.badge'
}

const offerings = {
	nextcloud: { key: 'nextcloud', icon: 'archive', to: '/nextcloud' },
	cloudron: { key: 'cloudron', icon: 'server', to: '/cloudron' },
	websites: { key: 'websites', icon: 'code', to: '/websites' },
	// Organisationen: Webseiten plus own web projects on the Epic Stack.
	webProjects: { key: 'webProjects', icon: 'code', to: '/websites' },
	coaching: {
		key: 'coaching',
		icon: 'lightbulb',
		to: '/coaching',
		badgeKey: 'home.offerings.coaching.badge',
	},
	shop: { key: 'shop', icon: 'laptop', to: '/shop', external: true },
} satisfies Record<Offering['key'], Offering>

// Webseiten sits in both lanes by decision (OS-450); Organisationen get it
// as webProjects, priced on request and naming the Epic Stack.
const lanes = [
	{
		key: 'organisationen',
		offerings: [offerings.cloudron, offerings.nextcloud, offerings.webProjects],
	},
	{
		key: 'privatpersonen',
		offerings: [offerings.coaching, offerings.shop, offerings.websites],
	},
] as const

export default function Index() {
	const localePath = useLocalizedPath()
	const { t } = useTranslation()
	return (
		<>
			{/* Hero: poster layout over the SpotlightStage. The pills and the CTAs
			    carry data-ripple, so hovering them sends a ripple through the dots.
			    The text blocks carry data-spotlight-keepout, so the beam and the
			    ripples stay out of their boxes. */}
			<section className="bg-band relative flex min-h-screen flex-col overflow-hidden">
				<div
					className="bg-dot-pattern absolute inset-0 opacity-[0.05]"
					aria-hidden="true"
				/>
				<SpotlightStage />

				<div className="relative z-10 container flex flex-1 flex-col pt-28 pb-14">
					<Reveal animation="fade-in">
						<ul className="flex flex-wrap gap-3" data-spotlight-keepout="top">
							{trustItems.map((item) => (
								<li
									key={item.labelKey}
									data-ripple
									className={cn(
										glassControlClassName,
										'text-body-xs text-foreground hover:border-brand flex items-center gap-2 rounded-full border px-4 py-2 font-semibold transition-colors',
									)}
								>
									<Icon name={item.icon} size="sm" className="text-primary" />
									{t(item.labelKey)}
								</li>
							))}
						</ul>
					</Reveal>

					<div className="min-h-56 flex-1" />

					<div className="grid items-end gap-10 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
						<Reveal>
							<h1
								className="text-h1 md:text-poster font-bold tracking-tight"
								data-spotlight-keepout="bottom"
							>
								<span className="text-gradient-brand">
									{t('home.heroHeading')}
								</span>
							</h1>
						</Reveal>

						<Reveal delay={100} className="lg:pb-3">
							<div data-spotlight-keepout="bottom">
								<p className="text-body-md text-muted-foreground lg:text-body-lg">
									{t('home.heroSubtitle')}
								</p>
								<div className="mt-8 flex flex-col gap-4 sm:flex-row">
									<Button asChild size="lg">
										<Link
											data-ripple
											to={localePath('/contact')}
											onClick={() => trackEvent('cta', 'click', 'contact-hero')}
										>
											{t('cta.contact')}
										</Link>
									</Button>
									<Button asChild size="lg" variant="outline">
										<a
											data-ripple
											href="/shop"
											onClick={() => trackEvent('cta', 'click', 'shop-hero')}
										>
											{t('home.ctaShop')}
										</a>
									</Button>
								</div>
							</div>
						</Reveal>
					</div>
				</div>
			</section>

			{/* Audience lanes: one chapter per lane, the panel pinned beside them */}
			<section className="py-24">
				<div className="container">
					<ChapterStage
						indexLabel={t('home.lanes.index')}
						chapters={lanes.map((lane) => ({
							key: lane.key,
							eyebrow: t(`home.lanes.${lane.key}.eyebrow`),
							title: t(`home.lanes.${lane.key}.title`),
							lead: t(`home.lanes.${lane.key}.lead`),
							icons: lane.offerings.map((offering) => offering.icon),
							body: (
								<ul className="border-border border-b">
									{lane.offerings.map((offering, i) => (
										<li key={offering.key} className="border-border border-t">
											<Reveal delay={i * 100}>
												<OfferingRow
													offering={offering}
													lane={lane.key}
													index={i}
												/>
											</Reveal>
										</li>
									))}
								</ul>
							),
						}))}
					/>
				</div>
			</section>

			{/* Epic Stack feature band: the lead paints itself as it scrolls by */}
			<section className="border-border bg-band border-y py-24">
				<div className="container">
					<div className="mx-auto max-w-4xl text-center">
						<p className="text-body-sm text-primary font-semibold tracking-wide uppercase">
							<WordReveal text={t('home.epicStack.eyebrow')} />
						</p>
						<h2 className="text-h2 mt-3 font-bold">
							<WordReveal
								text={t('epicStack.heading')}
								delay={150}
								wordClassName="text-gradient-brand"
							/>
						</h2>
						<PaintedText
							text={t('epicStack.lead')}
							className="text-h3 md:text-h2 mx-auto mt-8 font-medium tracking-tight"
						/>
						<Reveal delay={100}>
							<div className="mt-12 flex justify-center">
								<Magnetic>
									<Button asChild size="lg">
										<Link
											to={localePath('/websites#epic-stack')}
											onClick={() =>
												trackEvent('epic-stack', 'click', 'home-band')
											}
										>
											{t('home.epicStack.cta')}
											<Icon name="arrow-right" size="sm" />
										</Link>
									</Button>
								</Magnetic>
							</div>
						</Reveal>
					</div>
				</div>
			</section>

			{/* Partner logos */}
			<section className="bg-band py-24">
				<div className="container text-center">
					<Reveal>
						<h2 className="text-h2 mb-10 font-bold">
							<span className="text-gradient-brand">
								{t('home.partnersHeading')}
							</span>
						</h2>
					</Reveal>
					<LogoSpotlight
						labels={{
							kunde: t('home.partnerKind.kunde'),
							partner: t('home.partnerKind.partner'),
						}}
					/>
				</div>
			</section>

			{/* CTA section */}
			<CtaBand
				heading={t('home.ctaHeading')}
				text={t('home.ctaSubheading')}
				action={
					<Button asChild size="lg" variant="white">
						<Link
							to={localePath('/contact')}
							onClick={() => trackEvent('cta', 'click', 'contact-footer')}
						>
							{t('cta.contact')}
						</Link>
					</Button>
				}
			/>
		</>
	)
}

function OfferingRow({
	offering,
	lane,
	index,
}: {
	offering: Offering
	lane: (typeof lanes)[number]['key']
	index: number
}) {
	const localePath = useLocalizedPath()
	const { t } = useTranslation()
	const handleClick = () =>
		trackEvent('offering', 'click', `${lane}-${offering.key}`)

	const content = (
		<>
			<span
				aria-hidden="true"
				className="text-body-xs text-muted-foreground pt-1.5 font-mono tabular-nums"
			>
				{chapterNumber(index)}
			</span>
			<IconChip
				icon={offering.icon}
				size="lg"
				className="duration-medium ease-out-expo transition-transform group-hover:scale-110 group-hover:-rotate-6"
			/>
			<div className="min-w-0 flex-1">
				<h3 className="text-h4 flex flex-wrap items-center gap-3">
					{t(`home.offerings.${offering.key}.title`)}
					{offering.badgeKey ? <Badge>{t(offering.badgeKey)}</Badge> : null}
				</h3>
				<p className="text-body-sm text-primary mt-1 font-semibold">
					{t(`home.offerings.${offering.key}.subtitle`)}
				</p>
				<p className="text-body-md text-muted-foreground mt-3 max-w-prose">
					{t(`home.offerings.${offering.key}.description`)}
				</p>
			</div>
			<Icon
				name="arrow-right"
				className="text-primary duration-medium ease-out-expo mt-2 shrink-0 -translate-x-3 opacity-0 transition-[translate,opacity] group-hover:translate-x-0 group-hover:opacity-100"
			/>
		</>
	)
	const className =
		'group focus-cosy hover:bg-muted/60 duration-medium -mx-4 flex items-start gap-4 rounded-xl px-4 py-7 transition-colors sm:gap-6'

	return offering.external ? (
		<a href={offering.to} onClick={handleClick} className={className}>
			{content}
		</a>
	) : (
		<Link
			to={localePath(offering.to)}
			onClick={handleClick}
			className={className}
		>
			{content}
		</Link>
	)
}
